"use client";

import {
  createEventDialogOpenAtom,
  createEventEndTimeAtom,
  createEventResourceAtom,
  createEventStartTimeAtom,
  eventDialogOpenAtom,
  eventEditModeAtom,
  selectedEventAtom,
} from "@/atoms/calendarAtoms";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Event, Group, Workspace } from "@/db/schema";
import { authClient } from "@/lib/auth-client";
import { useAtom } from "jotai";
import { useMemo } from "react";
import { Button } from "../ui/button";
import { CreateEventDialog } from "./CreateEventDialog";
import { DeleteEventButton } from "./DeleteEventButton";
import { EditEventForm } from "./EditEventForm";

export function CalendarDialogWrapper({
  workspace,
  group,
  children,
  attendeeMembers = [],
  syncedResourceIds = [],
}: {
  workspace: Workspace;
  group: Group;
  children: React.ReactNode;
  /** Workspace members offered in the attendee picker (synced resources only). */
  attendeeMembers?: Array<{ email: string; name: string }>;
  syncedResourceIds?: string[];
}) {
  const [createDialogOpen, setCreateDialogOpen] = useAtom(createEventDialogOpenAtom);
  const [resource] = useAtom(createEventResourceAtom);
  const [startTime, setStartTime] = useAtom(createEventStartTimeAtom);
  const [endTime, setEndTime] = useAtom(createEventEndTimeAtom);
  const [selectedEvent] = useAtom(selectedEventAtom);
  const [eventDialogOpen, setEventDialogOpen] = useAtom(eventDialogOpenAtom);
  const [editMode, setEditMode] = useAtom(eventEditModeAtom);

  return (
    <>
      {children}

      {resource && (
        <CreateEventDialog
          open={createDialogOpen}
          setOpen={setCreateDialogOpen}
          resource={resource}
          startTime={startTime}
          endTime={endTime}
          setStartTime={setStartTime}
          setEndTime={setEndTime}
          attendeeMembers={syncedResourceIds.includes(resource.id) ? attendeeMembers : undefined}
        />
      )}

      {/* Event Details/Edit Dialog */}
      {selectedEvent && (
        <Dialog open={eventDialogOpen} onOpenChange={setEventDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader className="text-start">
              <DialogTitle className="leading-5">
                {selectedEvent.title}
                <br />
              </DialogTitle>
              <DialogDescription className="flex flex-col">
                <span>
                  {new Date(selectedEvent.startDate).toLocaleTimeString("sv-SE", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}{" "}
                  -{" "}
                  {new Date(selectedEvent.endDate).toLocaleTimeString("sv-SE", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
                <span>
                  Booked by <span className="text-blue-125">{selectedEvent.authorName}</span> at{" "}
                  <span className="text-blue-125">{selectedEvent.authorCompany}</span>
                </span>
                {selectedEvent.dynamicFieldValues &&
                  Object.keys(selectedEvent.dynamicFieldValues).length > 0 && (
                    <span className="flex flex-col">
                      {Object.entries(selectedEvent.dynamicFieldValues).map(([fieldId, value]) => {
                        const fieldDef = group.dynamicFields?.find((f) => f.id === fieldId);
                        if (!fieldDef) return null;
                        if (!value || value.length === 0) return null;

                        return (
                          <span key={fieldId} className="text-sm">
                            <span className="">{fieldDef.name}:</span>{" "}
                            <span className="text-blue-125">{value}</span>
                          </span>
                        );
                      })}
                    </span>
                  )}
              </DialogDescription>
            </DialogHeader>
            <AttendeeList event={selectedEvent} />
            <div className="flex flex-col w-full space-y-2">
              {selectedEvent.source === "outlook" ? (
                <>
                  {selectedEvent.onlineMeetingUrl && (
                    <Button asChild variant="outline" className="w-full">
                      <a href={selectedEvent.onlineMeetingUrl} target="_blank" rel="noopener noreferrer">
                        Join online meeting
                      </a>
                    </Button>
                  )}
                  <p className="text-xs text-slate-70 text-center">
                    Managed in Outlook — edit or cancel this booking from the organizer&apos;s
                    calendar.
                  </p>
                </>
              ) : editMode ? (
                <EditEventForm
                  event={selectedEvent}
                  onCancel={() => {
                    setEventDialogOpen(false);
                  }}
                  groupDynamicFields={group.dynamicFields}
                />
              ) : (
                <>
                  <EditEventButton
                    event={selectedEvent}
                    action={() => {
                      setEditMode(true);
                    }}
                  />
                  <DeleteEventButton
                    requireAuth={workspace.requireAuth}
                    event={selectedEvent}
                    onDelete={() => {
                      setEventDialogOpen(false);
                    }}
                  />
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

/** Statuses are written only by the sync engine (accept/decline in Outlook). */
const AttendeeList = ({
  event,
}: {
  event: Event & { attendees?: Array<{ email: string; name: string | null; responseStatus: string }> };
}) => {
  if (!event.attendees?.length) return null;
  const variant = (status: string) =>
    status === "accepted"
      ? "default"
      : status === "declined"
        ? "destructive"
        : status === "tentativelyAccepted"
          ? "secondary"
          : "outline";
  const label = (status: string) =>
    status === "tentativelyAccepted" ? "tentative" : status === "none" || status === "notResponded" ? "invited" : status;
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-primary">Attendees</span>
      <div className="flex flex-wrap gap-1.5">
        {event.attendees.map((a) => (
          <Badge key={a.email} variant={variant(a.responseStatus)} className="font-normal">
            {a.name || a.email} · {label(a.responseStatus)}
          </Badge>
        ))}
      </div>
    </div>
  );
};

const EditEventButton = ({ event, action }: { event: Event; action: () => void }) => {
  const { data: activeMemberRole } = authClient.useActiveMemberRole();
  const { data: sessionData } = authClient.useSession();

  const admin = useMemo(() => {
    const role = activeMemberRole?.role;
    if (!role) return false;
    return role.split(",").some((r) => r === "admin" || r === "owner");
  }, [activeMemberRole?.role]);

  const creator = useMemo(() => {
    return sessionData?.user.id === event.clerkId;
  }, [sessionData?.user.id, event.clerkId]);

  if (admin || creator)
    return (
      <Button variant="outline" className="w-full" onClick={action}>
        Edit
      </Button>
    );
  return null;
};
